// main imports
import React, { Component } from "react";
import { FormattedMessage } from "react-intl";
import * as Auth from "@/lib/auth";
import Authorization from "@/modules/shared/Authorization";

// models
import * as t from "@/lib/backend/manufacturing2.generated.types";
import * as BusinessLogic from "@/lib/businessLogic";
import { renderTag, generateConsoleTable } from "@/lib/util";
import { getBackend } from "@/lib/backend/backend2";
import { getMaterialKnowledgeGraph, IMaterialKnowledgeGraph } from "@/lib/businessLogic";

import type { IOrderModelBase, IStockLocation, IProductionVersion } from "@/lib/backend/manufacturing2.generated.types";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";

type Props = {
    selected: t.IMaterialModel | null;
    selected_plant_tags: t.IMaterialPlantModel[] | null;
    parallel_selected: t.IParallelMaterialModel | null;
};

type State = {
    open_orders: IOrderModelBase[];
    stock_locations: IStockLocation[];
    prod_vers: IProductionVersion[];
    selected_prod_ver: IProductionVersion | null;
    prod_ver_details: t.IGetFullProductionVersionRes | null;
    material_knowledge_graph: IMaterialKnowledgeGraph;
    error: string | null;
};

class MaterialsDetails extends Component<Props, State> {
    state: State = {
        open_orders: [],
        stock_locations: [],
        material_knowledge_graph: {
            consumed_by_lines: [],
            consumed_by_materials: [],
            consumes_materials: [],
            produced_on_lines: [],
        },
        prod_vers: [],
        prod_ver_details: null,
        selected_prod_ver: null,
        error: null,
    };

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.selected !== this.props.selected) {
            this.load();
        }
    }

    async load() {
        if (!this.props.selected) {
            return null;
        }
        const { uuid: material_uuid, external_id: material_external_id } = this.props.selected;
        this.setState({ prod_vers: [], prod_ver_details: null, selected_prod_ver: null });
        try {
            const res = await getBackend().manufacturing.getOrdersSimple({
                material_uuids: [material_uuid],
                status: "open",
            });

            const stock_res = await getBackend().manufacturing.getStockLocationsForMaterial({ material_external_id });
            const material_knowledge_graph = await getMaterialKnowledgeGraph(getBackend().kg, material_external_id);

            const plants = BusinessLogic.getPlantsForUser();
            const prod_vers = this.state.prod_vers.slice(0, 0);
            for (const plant of plants) {
                const prodvers_for_plant = await getBackend().manufacturing.getProductionVersionForMaterialPlant({
                    material_uuid,
                    plant_uuid: plant.uuid,
                });
                prodvers_for_plant.production_versions.forEach(x => prod_vers.push(x));
            }

            this.setState({
                open_orders: res.operations,
                stock_locations: stock_res.stock_locations,
                material_knowledge_graph,
                prod_vers,
                error: null,
            });
        } catch (err) {
            this.setState({
                error: (err as Error).message,
            });
        }
    }

    async loadProdVerDetails(prodver: IProductionVersion) {
        this.setState({ prod_ver_details: null, selected_prod_ver: prodver });
        if (prodver === null) {
            return;
        }
        const res = await getBackend().manufacturing.getFullProductionVersion({ uuid: prodver.prodver_uuid });
        console.log({ res });
        this.setState({ prod_ver_details: res });
    }

    render() {
        const { selected, parallel_selected } = this.props;

        if (selected === null) {
            return null;
        }

        // Render material tags
        let tags: React.ReactNode = "/";
        if (selected.tags !== undefined || (parallel_selected !== null && parallel_selected.tags !== undefined)) {
            const selected_tags = parallel_selected !== null ? parallel_selected.tags : selected.tags;
            // generate tags
            tags = Object.keys(selected_tags)
                .sort((a, b) => a.localeCompare(b))
                .map((el, i) => renderTag(i, el, selected_tags[el]));
        }

        // prepare plant names
        const plants = BusinessLogic.getPlantsForUser();
        const plant_name_map: Map<string, t.IPlantData> = new Map();
        for (const plant of plants) {
            plant_name_map.set(plant.uuid, plant);
        }
        const is_dev_env = BusinessLogic.getSysFlag("env") === "dev";

        return (
            <div className="tab-content">
                <ErrorComponent msg={this.state.error} type={"error"} />
                <div className="tab-pane active tab-pane-text">
                    <div className="d-flex">
                        <div className="button-list order-last ml-auto" style={{ marginLeft: "auto" }}>
                            <Authorization.Link
                                className="btn btn-outline-primary"
                                to={`/digital-twin/resources/material/edit/${selected.uuid}`}
                                permission={Auth.PERMISSION_NAMES.MaterialEdit}
                            >
                                <FormattedMessage id="common.edit" defaultMessage="Edit" />
                            </Authorization.Link>
                        </div>

                        {parallel_selected !== null && (
                            <div className="button-list order-last">
                                <Authorization.Link
                                    className="btn btn-outline-primary"
                                    to={`/digital-twin/resources/material_parallel/edit/${parallel_selected.uuid}`}
                                    permission={Auth.PERMISSION_NAMES.MaterialEdit}
                                >
                                    <FormattedMessage
                                        id="common.edit_parallel"
                                        defaultMessage="Edit parallel materials"
                                    />
                                </Authorization.Link>
                            </div>
                        )}
                        <div className="order-first">
                            <h5 className="pane-title">
                                <em>
                                    <FormattedMessage id="common.material" defaultMessage="Material" />
                                </em>{" "}
                                {selected.title}
                            </h5>
                            <dl className="property-list">
                                <div>
                                    <dt>
                                        <FormattedMessage id="common.external_id" defaultMessage="External ID" />
                                    </dt>
                                    <dd>
                                        <div>{selected.external_id}</div>
                                    </dd>
                                </div>
                                <div>
                                    <dt>
                                        <FormattedMessage id="common.tags" defaultMessage="Tags" />
                                    </dt>
                                    <dd>{tags}</dd>
                                </div>
                                <div>
                                    <dt className="light">
                                        <FormattedMessage id="common.uuid" defaultMessage="UUID" />
                                    </dt>
                                    <dd className="light">
                                        <div>{selected.uuid}</div>
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                    <div className="card-deck">
                        {parallel_selected !== null && (
                            <div className="card">
                                <div className="card-header text-uppercase bg-transparent">
                                    <FormattedMessage
                                        id="Manufacturing.Materials.parallel_materials"
                                        defaultMessage="Multipart materials"
                                    />
                                </div>
                                <div className="card-body">
                                    <dl className="property-list">
                                        {parallel_selected.child_materials.map((cm, i) => (
                                            <div key={i}>
                                                <dt>{cm.external_id}</dt>
                                                <dd>{cm.title}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            </div>
                        )}
                    </div>
                    <br />
                    {this.props.selected_plant_tags !== null && this.props.selected_plant_tags.length > 0 && (
                        <div className="card-deck">
                            <div className="card">
                                <div className="card-header text-uppercase bg-transparent">
                                    <FormattedMessage id="common.tags" defaultMessage="Tags" />
                                </div>
                                <div className="card-body">
                                    <dl className="property-list">
                                        <div>
                                            {this.props.selected_plant_tags.map((spt, i) => {
                                                const plant = plant_name_map.get(spt.plant_uuid);
                                                if (plant !== undefined) {
                                                    return (
                                                        <div
                                                            key={i}
                                                            style={{
                                                                width: "100%",
                                                                height: "30px",
                                                                marginBottom: "5px",
                                                            }}
                                                        >
                                                            <dt>{plant.external_id}</dt>
                                                            <dt>{plant.title}</dt>
                                                            <dd>
                                                                {Object.keys(spt.tags).map((el, i) =>
                                                                    renderTag(i, el, spt.tags[el])
                                                                )}
                                                            </dd>
                                                            <Authorization.Link
                                                                className="btn btn-outline-primary"
                                                                style={{ float: "right" }}
                                                                to={`/digital-twin/resources/material_plant/edit/${selected.uuid}/${spt.plant_uuid}`}
                                                                permission={Auth.PERMISSION_NAMES.MaterialEdit}
                                                            >
                                                                <FormattedMessage
                                                                    id="common.edit"
                                                                    defaultMessage="Edit"
                                                                />
                                                            </Authorization.Link>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </div>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    )}
                    <br />
                    <div className="card-deck">
                        {this.state.open_orders && this.state.open_orders.length > 0 && (
                            <div className="card">
                                <div className="card-header text-uppercase bg-transparent">
                                    {translate("MaterialInfoModal.open_orders", "Open orders")}
                                </div>
                                <div className="card-body">
                                    <dl className="property-list">
                                        {this.state.open_orders.map((cm, i) => (
                                            <div key={i}>
                                                <dt>{cm.external_id}</dt>
                                                <dd>{cm.title}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="card-deck">
                        {this.state.stock_locations && this.state.stock_locations.length > 0 && (
                            <div className="card">
                                <div className="card-header text-uppercase bg-transparent">
                                    {translate("Header.menu.stock_locations", "Stock Locations")}
                                </div>
                                <div className="card-body" style={{ maxHeight: "40vh", overflow: "scroll" }}>
                                    <dl className="property-list">
                                        {this.state.stock_locations.map((cm, i) => (
                                            <div key={i}>
                                                <dt>{cm.external_id}</dt>
                                                <dd>{cm.title}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="card-deck">
                        <div className="card">
                            <div className="card-header text-uppercase bg-transparent">
                                <FormattedMessage
                                    id="MaterialInfoModal.consumes_materials"
                                    defaultMessage="Consumes materials"
                                />
                            </div>
                            <div className="card-body" style={{ maxHeight: "40vh", overflow: "scroll" }}>
                                <dl className="property-list">
                                    {this.state.material_knowledge_graph.consumes_materials.map((x, i) => (
                                        <div key={i}>
                                            <dt>{x.external_id}</dt>
                                            <dd>{x.title}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="card-deck">
                        <div className="card">
                            <div className="card-header text-uppercase bg-transparent">
                                <FormattedMessage
                                    id="MaterialInfoModal.consumed_by_materials"
                                    defaultMessage="Consumed by materials"
                                />
                            </div>
                            <div className="card-body" style={{ maxHeight: "40vh", overflow: "scroll" }}>
                                <dl className="property-list">
                                    {this.state.material_knowledge_graph.consumed_by_materials.map((x, i) => (
                                        <div key={i}>
                                            <dt>{x.external_id}</dt>
                                            <dd>{x.title}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="card-deck">
                        <div className="card">
                            <div className="card-header text-uppercase bg-transparent">
                                <FormattedMessage
                                    id="MaterialInfoModal.produced_on_lines"
                                    defaultMessage="Produced on lines"
                                />
                            </div>
                            <div className="card-body" style={{ maxHeight: "40vh", overflow: "scroll" }}>
                                <dl className="property-list">
                                    {this.state.material_knowledge_graph.produced_on_lines.map((x, i) => (
                                        <div key={i}>
                                            <dt>{x.external_id}</dt>
                                            <dd>{x.title}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="card-deck">
                        <div className="card">
                            <div className="card-header text-uppercase bg-transparent">
                                <FormattedMessage
                                    id="MaterialInfoModal.consumed_on_lines"
                                    defaultMessage="Consumed on lines"
                                />
                            </div>
                            <div className="card-body" style={{ maxHeight: "40vh", overflow: "scroll" }}>
                                <dl className="property-list">
                                    {this.state.material_knowledge_graph.consumed_by_lines.map((x, i) => (
                                        <div key={i}>
                                            <dt>{x.external_id}</dt>
                                            <dd>{x.title}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        </div>
                    </div>

                    {is_dev_env && (
                        <div className="card-deck">
                            <div className="card">
                                <div className="card-header text-uppercase bg-transparent">
                                    <FormattedMessage
                                        id="common.production_versions"
                                        defaultMessage="Production versions"
                                    />
                                </div>
                                <div className="card-body" style={{ maxHeight: "40vh", overflow: "scroll" }}>
                                    <ErrorComponent msg={"ONLY visible to admins in development mode"} type="warning" />
                                    <dl className="property-list">
                                        {this.state.prod_vers.map((x, i) => (
                                            <div key={i}>
                                                <dt>{x.prodver_external_id}</dt>
                                                <dd>
                                                    {x.title}{" "}
                                                    <button
                                                        className="btn btn-outline-primary"
                                                        onClick={() => this.loadProdVerDetails(x).catch(console.error)}
                                                    >
                                                        Details
                                                    </button>
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            </div>
                        </div>
                    )}

                    {is_dev_env && this.state.prod_ver_details !== null && (
                        <div className="card-deck">
                            <div className="card">
                                <div className="card-header text-uppercase bg-transparent">
                                    <h3>BOM</h3>
                                    <pre>
                                        {this.state.prod_ver_details !== null &&
                                            generateConsoleTable(this.state.prod_ver_details.bom.bom_items)}
                                    </pre>
                                    <h3>Routing</h3>
                                    <pre>
                                        {this.state.prod_ver_details !== null &&
                                            generateConsoleTable(this.state.prod_ver_details.routing.routing_items)}
                                    </pre>
                                    <h3>JSON</h3>
                                    <pre>{JSON.stringify(this.state.prod_ver_details, null, 2)}</pre>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default MaterialsDetails;
